import { useState, useEffect } from "react";
import {
  createCheckoutSession,
  checkSubscription,
  getManageSubscriptionUrl,
  checkIsExtensionInstalled,
  checkIsWhitelisted,
} from "../../services/api";
import { Layout } from "../Layout";
import { Navigate } from "react-router-dom";

const CHROME_STORE_URL =
  "https://chromewebstore.google.com/detail/birdie/jchflalokogblejikeiajkfolhobodbg";
const HELP_CENTER_URL = "https://birdie.helpscoutdocs.com/";

export function ManageSubscription({
  isAuthenticated,
  user,
}: {
  isAuthenticated: boolean;
  user?: { id: string; email: string };
}) {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [customerID, setCustomerID] = useState<string | null>(null);
  const [isWhitelisted, setIsWhitelisted] = useState<boolean>(false);
  const [hasSubscription, setHasSubscription] = useState<boolean>(false);
  const [isExtensionInstalled, setIsExtensionInstalled] =
    useState<boolean>(false);
  const [manageSubscriptionUrl, setManageSubscriptionUrl] =
    useState<string>("");
  const [isChecking, setIsChecking] = useState<boolean>(true);

  useEffect(() => {
    const checkWhitelist = async () => {
      if (!user) {
        return;
      }
      try {
        const isWhitelisted = await checkIsWhitelisted(user.email);
        setIsWhitelisted(isWhitelisted);
      } catch (err) {
        console.error("Failed to check whitelist:", err);
      } finally {
        setIsChecking(false);
      }
    };
    checkWhitelist();

    const checkUserSubscription = async () => {
      if (!isAuthenticated) {
        return;
      }
      try {
        const { hasSubscription, customerID } = await checkSubscription();
        setCustomerID(customerID);
        setHasSubscription(hasSubscription);
        if (hasSubscription) {
          const { url } = await getManageSubscriptionUrl();
          setManageSubscriptionUrl(url);
        }
      } catch (err) {
        console.error("Failed to check subscription status:", err);
      } finally {
        setIsChecking(false);
      }
    };
    checkUserSubscription();

    const checkExtensionInstalled = async () => {
      setIsExtensionInstalled(await checkIsExtensionInstalled());
    };
    checkExtensionInstalled();
  }, []);

  const handleActivate = async () => {
    try {
      setIsLoading(true);
      const { url } = await createCheckoutSession(
        customerID,
        customerID === null
      );
      window.location.href = url;
    } catch (err) {
      setError("Failed to start subscription process");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const openManageSubscription = () => {
    if (manageSubscriptionUrl) {
      window.location.href = manageSubscriptionUrl;
    } else {
      alert(
        `Your subscription ${
          user?.email ? `for ${user.email} ` : ""
        }is managed by another team member.\n\nPlease email hello@getbirdie.io for more help.`
      );
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (isChecking) {
    return (
      <Layout width="400px">
        <div className="text-center d-flex justify-content-center align-items-center flex-grow-1">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isWhitelisted && !hasSubscription) {
    return (
      <Layout width="400px">
        <div className="row flex-grow-1 justify-content-center">
          <div className="d-flex flex-column justify-content-between col-md-12">
            <div>
              <img
                src="/birdie_logo.svg"
                alt="Company Logo"
                style={{ height: "40px" }}
              />
            </div>
            <div style={{ marginTop: error ? "0" : "-40px" }}>
              <div className="font-size-large font-weight-bold mb-2">
                Activate your license
              </div>
              <div>
                {!customerID
                  ? "Start your free 2 week trial to Birdie. Cancel anytime."
                  : "Your trial has ended. Activate your license to continue using Birdie."}
              </div>
              {error && (
                <div className="alert alert-danger mt-3" role="alert">
                  {error}
                </div>
              )}
            </div>
            <div className="text-end">
              <button
                onClick={handleActivate}
                disabled={isLoading}
                className="btn btn-primary"
              >
                {isLoading ? (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : null}
                Activate
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isExtensionInstalled) {
    return (
      <Layout>
        <div className="row flex-grow-1 justify-content-center">
          <div className="col d-flex" style={{ position: "relative" }}>
            <img
              src="/birdie_logo.svg"
              alt="Company Logo"
              style={{ height: "40px", position: "absolute", top: "0" }}
            />
            <div>
              <div
                className="font-size-large font-weight-bold"
                style={{ marginTop: "120px" }}
              >
                Your license is active
              </div>
              <div>Follow the instructions on the right to get started.</div>
            </div>
          </div>
          <div className="col d-flex justify-content-between flex-column">
            <ol className="font-size-slightly-large mt-2">
              <li className="mb-4pt5">Install the Birdie Chrome Extension</li>
              <li className="mb-4pt5">Configure your settings</li>
              <li className="mb-4pt5">Start prospecting on LinkedIn</li>
            </ol>
            <div className="d-flex align-items-center justify-content-end">
              <a
                href="#"
                className="text-decoration-none me-4"
                onClick={openManageSubscription}
                rel="noopener noreferrer"
              >
                Subscription
              </a>
              <a
                href={HELP_CENTER_URL}
                className="text-decoration-none me-4"
                target="_blank"
                rel="noopener noreferrer"
              >
                Help Center
              </a>
              <a
                href={CHROME_STORE_URL}
                className="btn btn-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Install Birdie
              </a>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout width="400px">
      <div className="row flex-grow-1 justify-content-center">
        <div className="d-flex flex-column justify-content-between col-md-12">
          <div>
            <img
              src="/birdie_logo.svg"
              alt="Company Logo"
              style={{ height: "40px" }}
            />
          </div>
          <div style={{ marginTop: error ? "0" : "-40px" }}>
            <div className="font-size-large font-weight-bold mb-2">
              All Systems Go 🚀
            </div>
            <div>
              You're all set to start prospecting on LinkedIn. Check out our
              help articles to learn how Birdie can supercharge your
              productivity on LinkedIn.
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-end">
            <a
              href="#"
              className="text-decoration-none me-4"
              onClick={openManageSubscription}
              rel="noopener noreferrer"
            >
              Manage Subscription
            </a>
            <button
              onClick={() => (window.location.href = HELP_CENTER_URL)}
              disabled={isLoading}
              className="btn btn-primary"
            >
              How to use Birdie
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
